import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSession } from '@/app/session';
import { StatusLine } from '@/features/common/StatusLine';
import { useStatusLine } from '@/features/common/useStatusLine';
import { detailOr } from '@/services/api';
import { publicCatalogApi } from '@/features/publicsite/catalog/api/publicCatalogApi';
import {
  DISCUSSION_STATE,
  discussionState,
  discussionSummaryKey,
  topicPath,
} from '@/features/publicsite/catalog/projectDiscussion';
import type { ProjectDiscussion } from '@/features/publicsite/types/community/ProjectDiscussion';
import type { ProjectDiscussionSectionProps } from '@/features/publicsite/types/research/ProjectDiscussionSectionProps';
import { publicCatalog } from '@/i18n/dictionaries/publicCatalog';
import { useTranslation } from '@/i18n/useTranslation';

/**
 * "Schimb de experiență" on a research project's page — ToR §6.7's fourth work flow,
 * "integrarea cu instrumente interactive pentru schimb de experiență".
 *
 * §6.13 built the forums and §6.7 asks this module to be integrated with them; nothing joined
 * the two. A reader with a question about a project had to leave it, find the community section,
 * guess a forum and open a topic that nobody arriving at the project afterwards could find.
 */
export function ProjectDiscussionSection({ projectId }: ProjectDiscussionSectionProps) {
  const { t } = useTranslation(publicCatalog);
  const { user } = useSession();
  const [discussion, setDiscussion] = useState<ProjectDiscussion | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { status: error, statusKind, fail, clear } = useStatusLine();

  useEffect(() => {
    let current = true;
    setLoaded(false);
    setFailed(false);
    // Reset with the project, not just the request. The router keeps this component mounted when
    // only :slug changes, so a draft typed under project A survived into project B's box and the
    // next press would have posted it there — the wrong conversation, in the reader's name.
    setDiscussion(null);
    setMessage('');
    clear();
    // The server answers 204 when nobody has started one, and apiGet turns that into undefined
    // (api.ts:115) — normalised to null here. That distinction is what this block turns on:
    // "nobody has started one" is a state to offer something for, not a failed request.
    void publicCatalogApi
      .discussion(projectId)
      .then((d) => {
        if (current) setDiscussion(d ?? null);
      })
      // Told apart from 204 rather than folded into it. Treating a refused read as "nobody has
      // started one" tells every reader the conversation does not exist and takes away the link
      // into the one that does — the worst of the four states to guess wrong.
      .catch(() => {
        if (current) setFailed(true);
      })
      .finally(() => {
        if (current) setLoaded(true);
      });
    return () => {
      current = false;
    };
  }, [projectId, clear]);

  const start = async () => {
    if (!message.trim()) return;
    setSubmitting(true);
    clear();
    try {
      const started = await publicCatalogApi.startDiscussion(projectId, message.trim());
      setDiscussion(started);
      setMessage('');
    } catch (e) {
      fail(detailOr(e, t('discussionFailed')));
    } finally {
      setSubmitting(false);
    }
  };

  const state = discussionState(discussion, loaded, !!user, failed);

  return (
    <div className="project-discussion">
      <div className="section-head">
        <div>
          <h2>{t('discussionHeading')}</h2>
        </div>
      </div>

      {state === DISCUSSION_STATE.loading && <p className="muted">{t('loading')}</p>}

      {state === DISCUSSION_STATE.failed && <p className="muted">{t('discussionUnavailable')}</p>}

      {state === DISCUSSION_STATE.open && discussion && (
        <>
          <p>{t(discussionSummaryKey(discussion), { count: discussion.replies })}</p>
          <p>
            <Link
              className="btn btn--primary"
              to={topicPath(discussion.forumSlug, discussion.topicId)}
            >
              {t('discussionOpen')}
            </Link>
          </p>
        </>
      )}

      {state === DISCUSSION_STATE.signin && (
        <p>
          {t('discussionSignIn')} <a href="/saml/authenticate/mpass">{t('discussionSignInLink')}</a>
        </p>
      )}

      {state === DISCUSSION_STATE.start && (
        <>
          <p>{t('discussionStartLead')}</p>
          <StatusLine status={error} kind={statusKind} />
          <label className="project-discussion__label" htmlFor="project-discussion-message">
            {t('discussionMessageLabel')}
          </label>
          <textarea
            id="project-discussion-message"
            className="project-discussion__input"
            rows={3}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
          <p>
            {/* Disabled until there is something to say: the whole design rests on a topic never
                being created empty, and an enabled button that silently does nothing is a worse
                way to enforce it than one that shows it cannot be pressed. */}
            <button
              className="btn btn--primary"
              disabled={submitting || !message.trim()}
              onClick={() => void start()}
            >
              {t('discussionStart')}
            </button>
          </p>
        </>
      )}
    </div>
  );
}
